import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from './ConfirmDialog';
import { LabeledTextarea } from './LabeledTextarea';

interface DeletePostDialogProps {
  isOpen: boolean;
  // Moderator removal (someone else's post) vs. the author deleting their own
  isModeratorRemoval: boolean;
  isSyncedWithWebsite?: boolean;
  postId?: string;
  onConfirm: (reason?: string) => void;
  onCancel: () => void;
  onClosed?: () => void;
  isLoading?: boolean;
}

export const DeletePostDialog: FC<DeletePostDialogProps> = ({
  isOpen,
  isModeratorRemoval,
  isSyncedWithWebsite = false,
  postId,
  onConfirm,
  onCancel,
  onClosed,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');

  return (
    <ConfirmDialog
      isOpen={isOpen}
      title={
        isModeratorRemoval
          ? t('community.action_menu.remove_post_confirm_title')
          : t('community.action_menu.delete_post_confirm_title')
      }
      description={
        isModeratorRemoval
          ? t('community.action_menu.remove_post_confirm_description')
          : t('community.action_menu.delete_post_confirm_description')
      }
      confirmLabel={
        isModeratorRemoval ? t('comments.removing.remove') : t('community.action_menu.delete_post_confirm_button')
      }
      onConfirm={() => onConfirm(reason.trim() || undefined)}
      confirmDisabled={isModeratorRemoval && reason.trim().length === 0}
      onCancel={onCancel}
      afterLeave={() => {
        // Reset here (not via a remount key) so the dialog keeps its instance through the
        // fade-out; clearing on close means the reason is empty on the next open.
        setReason('');
        onClosed?.();
      }}
      isDestructive
      isLoading={isLoading}
      body={
        <>
          {isSyncedWithWebsite && (
            <p className="mt-3 text-base leading-6 text-black/60">
              {t('community.action_menu.delete_synced_post_warning')}
            </p>
          )}
          {isModeratorRemoval && (
            <LabeledTextarea
              id={`remove-post-reason-${postId}`}
              label={t('community.action_menu.remove_reason_label')}
              maxLength={2000}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t('community.action_menu.remove_reason_placeholder')}
              containerClassName="mt-4"
              required
            />
          )}
        </>
      }
    />
  );
};
